import { Injectable } from '@angular/core'
import { SignFieldDef } from '@tokeet-frontend/signature'

export type FieldActionData = Partial<SignFieldDef>

export type FieldActionType = 'move' | 'resize' | 'add' | 'remove' | 'settings'

export interface FieldAction {
  type: FieldActionType
  fieldId: string
  after?: FieldActionData
  before?: FieldActionData
}

export type FieldEditorAction = FieldAction[]

export declare interface ManagedFieldAction {
  undo(): void
  redo(): void
}

@Injectable({ providedIn: 'root' })
export class FieldsEditorActionManagerService {
  private actionStack: FieldEditorAction[] = []
  private redoStack: FieldEditorAction[] = []

  constructor() {}

  private record(action: FieldEditorAction) {
    this.actionStack.push(action)
    // clear all redo actions on this field
    const ids = action.map((a) => a.fieldId)
    this.redoStack = this.redoStack
      .map((items) => {
        return items.filter((a) => !ids.includes(a.fieldId))
      })
      .filter((t) => !t?.length)
  }

  get isRedoable() {
    return !!this.redoStack.length
  }

  get isUndoable() {
    return !!this.actionStack.length
  }

  clear() {
    this.actionStack = []
    this.redoStack = []
  }

  doMove(id: string, after: FieldActionData, before: FieldActionData) {
    this.record([{ type: 'move', fieldId: id, before, after }])
  }

  batch(action: FieldEditorAction) {
    this.record(action)
  }

  doResize(id: string, after: FieldActionData, before: FieldActionData) {
    this.record([{ type: 'resize', fieldId: id, after, before }])
  }

  doAdd(id: string, data: FieldActionData) {
    this.record([{ type: 'add', fieldId: id, after: data }])
  }

  doRemove(id: string, data: FieldActionData) {
    this.record([{ type: 'remove', fieldId: id, before: data }])
  }

  doUpdateSettings(id: string, after: FieldActionData, before: FieldActionData) {
    this.record([{ type: 'settings', fieldId: id, after, before }])
  }

  getUndoData(): FieldEditorAction {
    const action = this.actionStack.pop()
    this.redoStack.push(action)
    return action
  }

  getRedoData(): FieldEditorAction {
    const action = this.redoStack.pop()
    this.actionStack.push(action)
    return action
  }
}
